import { Card, CardContent, CardHeader } from '@/components/ui/card';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

interface AppCardProps {
  title: string;
  description: string;
  Icon: LucideIcon;
  colorScheme?: string;
  provider?: string;
}

const colorMap = {
  blue: { bg: 'bg-blue-50', text: 'text-blue-600' },
  emerald: { bg: 'bg-emerald-50', text: 'text-emerald-600' },
  purple: { bg: 'bg-purple-50', text: 'text-purple-600' },
  orange: { bg: 'bg-orange-50', text: 'text-orange-600' },
  slate: { bg: 'bg-slate-50', text: 'text-slate-600' },
  indigo: { bg: 'bg-indigo-50', text: 'text-indigo-600' },
  zinc: { bg: 'bg-zinc-50', text: 'text-zinc-600' },
} as const;

export const AppCard = ({
  title,
  description,
  Icon,
  colorScheme = 'zinc',
  provider,
}: AppCardProps) => {
  const colors = colorMap[colorScheme as keyof typeof colorMap] || colorMap.zinc;

  return (
    <Card className="transition-all duration-300 hover:shadow-sm hover:-translate-y-0.5 cursor-pointer border-gray-200 dark:border-zinc-700">
      <CardHeader className="flex flex-row items-center gap-3 pb-1 pt-3 px-3">
        <div className={cn('p-1.5 mt-1.5 rounded-md flex-shrink-0', colors.bg)}>
          <Icon className={cn('w-4 h-4', colors.text)} />
        </div>
        <div className="flex-1">
          <h3 className="font-medium text-sm text-zinc-800 dark:text-zinc-200 line-clamp-1">
            {title}
          </h3>
          {provider && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
              {provider.charAt(0).toUpperCase() + provider.slice(1)}
            </span>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-1 pb-3 px-3">
        <p className="text-xs text-zinc-500 line-clamp-2">{description}</p>
      </CardContent>
    </Card>
  );
};
